import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { authClient } from "../lib/auth-client";
import { clearLandingPrompt, readLandingPrompt } from "../lib/landingPrompt";
import { SignInView, type OAuthProvider } from "../components/SignInView";
import { readLastProvider, rememberLastProvider } from "../lib/signInMethods";
import { PRODUCT_UPDATES_PARAM } from "../lib/useEmailPreferences";

export const Route = createFileRoute("/sign-in")({
  component: SignIn,
});

function SignIn() {
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<OAuthProvider | null>(null);
  const [productUpdates, setProductUpdates] = useState(false);
  // main.tsx renders over the prerendered page (createRoot, no hydration),
  // so reading storage during the first render is safe.
  const [lastUsed] = useState(() => readLastProvider());
  const navigate = useNavigate();

  // Already signed in: on to the workspace. Checked after the first paint,
  // not in beforeLoad, so a signed-out visitor sees the page without
  // waiting on the API.
  useEffect(() => {
    let cancelled = false;
    void authClient.getSession().then(
      (session) => {
        if (cancelled || !session.data) return;
        const prompt = readLandingPrompt();
        clearLandingPrompt();
        void navigate({
          to: "/",
          replace: true,
          state: { landingPrompt: prompt ?? undefined } as never,
        });
      },
      () => undefined
    );
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // The opt-in rides on the callback URL, so it needs no cookie or storage;
  // /auth/callback saves it once the session exists.
  const authCallbackUrl = `${window.location.origin}/auth/callback${
    productUpdates ? `?${PRODUCT_UPDATES_PARAM}=1` : ""
  }`;

  async function signInWithOAuth(provider: OAuthProvider) {
    setErr(null);
    setBusy(provider);
    rememberLastProvider(provider);

    try {
      await authClient.signIn.social({
        provider,
        callbackURL: authCallbackUrl,
      });
    } catch (error) {
      setBusy(null);
      setErr(error instanceof Error ? error.message : "OAuth sign-in failed");
    }
  }

  return (
    <SignInView
      err={err}
      busy={busy}
      lastUsed={lastUsed}
      productUpdates={productUpdates}
      onProductUpdatesChange={setProductUpdates}
      onSignIn={(provider) => void signInWithOAuth(provider)}
    />
  );
}
